import { hasParentNodeOfType } from '../utils/findNodes';
import { getValidUrl } from '../utils/url';
import { lift, wrapIn, toggleMark } from 'prosemirror-commands';
import { undo as undoCmd, redo as redoCmd } from 'prosemirror-history';
import {
  createHeadingItem,
  createParagraphItem,
  createMarkItem,
  createMenuItem,
  createListItem,
  createImageItem
} from './MenuItem';
import richSchema from '../richSchema';
import { currentMarkRange } from '../utils/selection';
import { iframeSubmitCallback } from './iframeSubmitCallback';
import clearFormattingCommand from './clearFormattingCommand';

export const blockquote = createMenuItem({
  command: (state, dispatch) => {
    const active = hasParentNodeOfType(richSchema.nodes.blockquote)(state.selection);
    active ? lift(state, dispatch) : wrapIn(richSchema.nodes.blockquote)(state, dispatch);
  },
  name: 'blockquote',
  status: 'enabled',
  statusCheck: (state) => {
    if (state.selection.node && state.selection.node.type.name === 'image_with_link') {
      return 'disabled';
    }
    return hasParentNodeOfType(richSchema.nodes.blockquote)(state.selection) ? 'active' : 'enabled';
  },
  spec: richSchema.nodes.blockquote
});

export const bold = createMarkItem({ spec: richSchema.marks.strong, name: 'b' });

export const em = createMarkItem({ spec: richSchema.marks.em, name: 'i' });

export const h1 = createHeadingItem(1, richSchema);

export const h2 = createHeadingItem(2, richSchema);

export const h3 = createHeadingItem(3, richSchema);

export const h4 = createHeadingItem(4, richSchema);

export const h5 = createHeadingItem(5, richSchema);

export const h6 = createHeadingItem(6, richSchema);

export const indent = (state, dispatch, editorView) => {
  const { from } = state.selection;
  dispatch(state.tr.insertText('\u2003', from));
  if (editorView) editorView.focus();
  return true;
};

export const link = createMarkItem({
  spec: richSchema.marks.link,
  name: 'link',
  statusCheck: (state) => {
    let { from, to, empty, node } = state.selection;
    if ((node && node.type.name === 'image_with_link') || empty) return 'disabled';
    return state.doc.rangeHasMark(from, to, richSchema.marks.link) ? 'disabled' : 'enabled';
  },
  command: (state, dispatch) =>
    dispatch(state.tr.setMeta('prompt-menu-plugin', { type: 'link' })),
  onSave: (editorView, attrMap) => {
    const existingMarkRange = currentMarkRange(
      editorView.state.selection.$from.doc,
      editorView.state.selection.from,
      editorView.state,
      richSchema.marks.link
    );

    const { tr } = editorView.state;

    let { href } = attrMap;
    href = getValidUrl(href);
    attrMap = { ...attrMap, href };

    // Editing existing mark or creating a new one?
    if (existingMarkRange) {
      tr.removeMark(existingMarkRange.from, existingMarkRange.to, richSchema.marks.link);
      tr.addMark(
        existingMarkRange.from,
        existingMarkRange.to,
        richSchema.marks.link.create(attrMap)
      );
      editorView.dispatch(tr.scrollIntoView());
    } else {
      toggleMark(richSchema.marks.link, attrMap)(editorView.state, editorView.dispatch);
    }

    editorView.dispatch(editorView.state.tr.setMeta('prompt-menu-plugin', ''));
    editorView.focus();
  }
});

export const iframe = createMenuItem({
  spec: richSchema.nodes.iframe,
  name: 'iframe',
  modalHeader: 'Insert a video or iframe',
  status: 'enabled',
  statusCheck: (state) => {
    let { from, $from, to, empty, node } = state.selection;
    if (node && node.type.name === 'image_with_link') {
      return 'disabled';
    } else {
      const active = empty
        ? richSchema.marks.link.isInSet(state.storedMarks || $from.marks())
        : state.doc.rangeHasMark(from, to, richSchema.marks.link);

      return active ? 'disabled' : 'enabled';
    }
  },
  command: (state, dispatch) =>
    dispatch(state.tr.setMeta('prompt-menu-plugin', { type: 'iframe' })),
  onSave: (editorView, attrMap) => {
    iframeSubmitCallback(attrMap, editorView, richSchema);
  }
});

export const orderedList = createListItem({
  spec: richSchema.nodes.orderedList,
  name: 'OL',
  listItemNodeSpec: richSchema.nodes.list_item,
  disableIfNodeSpec: richSchema.nodes.bulletList
});

export const paragraph = createParagraphItem({ spec: richSchema.nodes.paragraph, name: 'p' });

export const redo = createMenuItem({
  command: redoCmd,
  name: 'redo',
  status: 'disabled',
  statusCheck: (state) => (redoCmd(state) ? 'enabled' : 'disabled')
});

export const strike = createMarkItem({ spec: richSchema.marks.strike, name: 'strike' });

export const superscript = createMarkItem({
  spec: richSchema.marks.superscript,
  name: 'superscript'
});

export const underline = createMarkItem({
  spec: richSchema.marks.underline,
  name: 'underline'
});

export const undo = createMenuItem({
  command: undoCmd,
  name: 'undo',
  status: 'disabled',
  statusCheck: (state) => (undoCmd(state) ? 'enabled' : 'disabled')
});

export const unorderedList = createListItem({
  spec: richSchema.nodes.bulletList,
  name: 'UL',
  listItemNodeSpec: richSchema.nodes.list_item,
  disableIfNodeSpec: richSchema.nodes.orderedList
});

export const image = createImageItem({
  name: 'image',
  spec: richSchema.nodes.image_with_link,
  command: (state, dispatch) =>
    dispatch(state.tr.setMeta('prompt-menu-plugin', { type: 'image' })),
  onSave: (editorView, attrMap) => {
    const { target, img_src, ...attrs } = attrMap;
    attrs.src = img_src;
    if (attrs.href) attrs.target = attrMap.target;

    editorView.dispatch(
      editorView.state.tr.replaceSelectionWith(richSchema.nodes.image_with_link.create(attrs))
    );
    editorView.dispatch(editorView.state.tr.setMeta('prompt-menu-plugin', ''));
    editorView.focus();
  }
});

export const clearFormatting = createMenuItem({
  command: (state, dispatch, editorView) => {
    clearFormattingCommand(state, editorView);
  },
  name: 'clearFormatting',
  status: 'enabled',
  statusCheck: (state) => {
    return 'enabled';
  }
});



// WEBPACK FOOTER //
// ./app/javascript/client/editor-core/plugins/menuItems.js